import React from 'react';
import classNames from 'classnames';
import { FieldType, ParticipantFieldType, Participation } from '@app/api';
import { TableParticipationHeader } from '../../components/TableParticipationHeader';
import { Filter } from '../../components/Filter';
import { useParticipationTableContext } from './ParticipationTableContext';
import { TextLoadingPlaceholder, Tooltip } from '@app/core';
import { TableColumnHeader } from './TableColumnHeader';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useDateTimeFormatter } from 'pages/Dashboard/Sessions/Details/hooks/useDateTimeFormatter';

const TOOLTIP_DELAY = 800;

const useStyles = createUseStyles((theme: Theme) => ({
  dateTime: {
    display: 'flex',
    flexDirection: 'column',
  },
  dateTimeHour: {
    ...theme.fonts.calendarText,
  },
  important: {
    ...theme.fonts.label,
  },
  maxColumnSize: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: theme.marginBase * 35,
  },
}));

interface ParticipationDefaultHeaderProps {
  field: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  type?: FieldType | ParticipantFieldType;
}

export const ParticipationDefaultHeader = ({
  field,
  label,
  sortable,
  filterable,
  type,
}: ParticipationDefaultHeaderProps) => {
  const { session, sorting, filtering } = useParticipationTableContext();

  return (
    <th key={field}>
      <TableColumnHeader>
        <TableParticipationHeader
          fieldKey={field}
          fieldType={type || ParticipantFieldType.SHORT_TEXT}
          currentSort={sortable ? sorting.sort : undefined}
          onSortClick={sortable ? sorting.handleSortClick : undefined}
        >
          <span>{label}</span>
        </TableParticipationHeader>
        {filterable && (
          <Filter
            fieldRef={field}
            fieldLabel={label}
            fieldType={type || FieldType.SHORT_TEXT}
            sessionId={session.id}
            currentFilterData={filtering.getFieldCurrentFilter(field)}
            onFilterDataChange={filtering.handleFilterChange}
          />
        )}
      </TableColumnHeader>
    </th>
  );
};

interface ParticipationDefaultCellProps {
  field: string;
  type?: FieldType | ParticipantFieldType;
  participation: Participation;
  important?: boolean;
}

export const ParticipationDefaultCell = ({
  field,
  type,
  participation,
  important,
}: ParticipationDefaultCellProps) => {
  const { dateFormatter, timeFormatter } = useDateTimeFormatter();
  const classes = useStyles();
  const value = participation[field as keyof Participation];

  if (type === ParticipantFieldType.DATE && value) {
    return (
      <td key={field}>
        <div className={classes.dateTime}>
          <span>{dateFormatter.format(new Date(String(value)))}</span>
          <span className={classes.dateTimeHour}>
            {timeFormatter.format(new Date(String(value)))}
          </span>
        </div>
      </td>
    );
  }

  const displayValue = value ? String(value) : '';
  return (
    <td
      key={field}
      className={classNames(classes.maxColumnSize, {
        [classes.important]: important,
      })}
    >
      <Tooltip
        arrow
        placement="right"
        title={displayValue}
        enterDelay={TOOLTIP_DELAY}
        enterNextDelay={TOOLTIP_DELAY}
      >
        {<span>{displayValue}</span>}
      </Tooltip>
    </td>
  );
};

interface ParticipationDefaultLoaderProps {
  field: string;
  label: string;
}

export const ParticipationDefaultLoader = ({
  field,
  label,
}: ParticipationDefaultLoaderProps) => {
  return (
    <td key={field}>
      <TextLoadingPlaceholder sample={label.slice(0, 36)} />
    </td>
  );
};
